import { useCallback } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import ContentDetailsPage from 'content-management-details-lib/components/ContentDetailsPage';
import PortalIdParser from 'PortalIdParser';
import { getListingPageUrl } from 'page-lib/utils/urls';
import { OPTIMIZATION_TAB } from '../constants/tabs';
import OptimizationContainer from '../containers/OptimizationContainer';
import TestResultsContainer from '../containers/TestResultsContainer';
import useNavigation from '../hooks/useNavigation';
import OptimizationSidebar from './OptimizationSidebar';
import PerformanceCharts from './PerformanceCharts';
import Tabs from './Tabs';
import { CONTENT_PLATFORM_PROPERTY_NAME, ContentObjectTypes, getContentPropertyValue, getPropertyValue, useContentObjectType } from 'content-management-data-lib';
import { useContentDetailsDataProvider } from 'content-management-details-lib/providers/ContentDetailsDataProvider';
import { PAGE_PROPERTY_NAMES } from 'page-lib/constants/propertyNames';
import useMabExperiment from '../hooks/useMabExperiment';
import useAbVariants from '../hooks/useAbVariants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TestIcon } from 'foundations-assets';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tag } from 'foundations-components/transitional/components/Tag';
import useQueryParams from '../hooks/useQueryParams';
import SmartContentContainer from '../containers/SmartContentContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HUBSPOT_DOMAIN_REGEX = /^https?:\/\/(?:app|local)-?([a-z]+[0-9])?.hubspot(?:qa)?.com/;
const TestStatusTag = ({
  contentObject
}) => {
  const {
    contentObjectType
  } = useContentObjectType();
  const isLandingPage = contentObjectType === ContentObjectTypes.LANDING_PAGE;
  const mabExperimentId = getPropertyValue(contentObject, PAGE_PROPERTY_NAMES.MAB_EXPERIMENT_ID);
  const abTestId = getPropertyValue(contentObject, PAGE_PROPERTY_NAMES.AB_TEST_ID);
  const {
    hasMab
  } = useMabExperiment({
    mabExperimentId,
    isLandingPage
  });
  const {
    abVariants
  } = useAbVariants({
    abTestId,
    isLandingPage
  });
  const hasAbVariants = abVariants && abVariants.length > 0;
  if (!hasMab && !hasAbVariants) {
    return null;
  }
  const langKey = hasMab ? 'page-details-ui.header.contentStatusTag.mab' : 'page-details-ui.header.contentStatusTag.ab';
  return /*#__PURE__*/_jsxs(Tag, {
    "data-test-id": "test-variant-status-tag",
    marginInlineStart: "200",
    children: [/*#__PURE__*/_jsx(IconHolder, {
      icon: TestIcon,
      paddingBlock: "0",
      paddingInline: "0",
      marginInlineEnd: "100"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: langKey
    })]
  });
};
const PagesDetailsPage = ({
  useActionsConfig
}) => {
  const {
    contentObjectType
  } = useContentObjectType();
  const {
    contentObject
  } = useContentDetailsDataProvider();
  const {
    tabId,
    updateCurrentTab,
    updateCurrentPage
  } = useNavigation();
  const queryParams = useQueryParams();
  const actionsConfigOptions = useActionsConfig();
  const renderSidebar = useCallback(() => {
    if (tabId === OPTIMIZATION_TAB) {
      const contentUrl = getContentPropertyValue(contentObject, CONTENT_PLATFORM_PROPERTY_NAME.ABSOLUTE_URL);
      return /*#__PURE__*/_jsx(OptimizationSidebar, {
        contentUrl: contentUrl
      });
    }
    return null;
  }, [tabId, contentObject]);
  const getBackBreadcrumbHref = useCallback(() => {
    // TODO: route back to content staging once there is a way to determine if
    // a page is a staged page
    // if (contentIsStagedPage) {
    //   return ContentRoutes.staging();
    // }
    const portalId = PortalIdParser.get();
    const referrerUrl = queryParams.get('referrerUrl');
    if (referrerUrl && HUBSPOT_DOMAIN_REGEX.test(referrerUrl) && portalId && referrerUrl.includes(`${portalId}`)) {
      return referrerUrl;
    }
    return getListingPageUrl(contentObjectType);
  }, [queryParams, contentObjectType]);
  return /*#__PURE__*/_jsx(ContentDetailsPage, {
    getBackBreadcrumbHref: getBackBreadcrumbHref,
    renderSidebar: renderSidebar,
    onChangeLanguageVariation: updateCurrentPage,
    tabs: /*#__PURE__*/_jsx(Tabs, {
      currentTabSelection: tabId,
      updateCurrentSelection: updateCurrentTab
    }),
    actionsConfigOptions: actionsConfigOptions,
    socialActionsConfig: {
      hasSocialActions: true
    },
    additionalStatusTags: [TestStatusTag],
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        component: PerformanceCharts,
        path: "/:pageTypeSlug/:contentId/performance"
      }), /*#__PURE__*/_jsx(Route, {
        component: OptimizationContainer,
        path: "/:pageTypeSlug/:contentId/optimization"
      }), /*#__PURE__*/_jsx(Route, {
        component: TestResultsContainer,
        path: "/:pageTypeSlug/:contentId/testResults"
      }), /*#__PURE__*/_jsx(Route, {
        component: SmartContentContainer,
        path: "/:pageTypeSlug/:contentId/smartContent"
      }), /*#__PURE__*/_jsx(Redirect, {
        to: "/:pageTypeSlug/:contentId/performance"
      })]
    })
  });
};
export default PagesDetailsPage;